/**
 * Transient feedback for actions whose result is not otherwise visible on screen.
 *
 * A store rather than a context so that non-component code — a mutation's `onSuccess`,
 * an interceptor in the API client — can raise a toast without being passed a hook.
 */

import { create } from 'zustand';

export type ToastTone = 'default' | 'success' | 'warning' | 'destructive';

export interface Toast {
  id: string;
  title: string;
  description?: string;
  tone: ToastTone;
  /** Milliseconds before auto-dismiss. Errors default to staying put. */
  duration: number;
}

interface ToastState {
  toasts: Toast[];
  push: (toast: Omit<Toast, 'id' | 'tone' | 'duration'> & Partial<Pick<Toast, 'tone' | 'duration'>>) => string;
  dismiss: (id: string) => void;
}

let counter = 0;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],

  push: ({ tone = 'default', duration, ...rest }) => {
    counter += 1;
    const id = `toast-${counter}`;

    set((state) => ({
      toasts: [
        ...state.toasts,
        {
          id,
          tone,
          // A failure the user must read stays until dismissed; a confirmation
          // they already expected does not need to hold the corner of the screen.
          duration: duration ?? (tone === 'destructive' ? Number.POSITIVE_INFINITY : 4500),
          ...rest,
        },
        // Cap the stack: beyond three, the oldest expires before it can be read.
      ].slice(-3),
    }));

    return id;
  },

  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((item) => item.id !== id) })),
}));

/** Convenience wrappers so call sites read as intent rather than configuration. */
export const toast = {
  success: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, tone: 'success' }),
  error: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, tone: 'destructive' }),
  warning: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, tone: 'warning' }),
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description }),
};
